import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FileText, Download, FileSignature, ReceiptText, Image as ImageIcon, ClipboardList, File,
  Briefcase, CalendarDays, FolderOpen, TrendingUp, Check, Inbox, ChevronRight, Upload,
  Search, SlidersHorizontal, MoreVertical, Eye,
} from "lucide-react";
import api, { API } from "@/lib/api";
import { useCockpit } from "./CockpitLayout";
import { Card, BtnPrimary, SectionLabel } from "./ui";
import { Spinner, chfFr } from "@/components/common";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

const CAT_ICON = { vertrag: FileSignature, briefing: ClipboardList, rechnung: ReceiptText, bericht: FileText, bild: ImageIcon, sonstiges: File };
const CAT_TONE = { vertrag: "#8B5CF6", briefing: "#8B5CF6", rechnung: "#F59E0B", bericht: "#3B82F6", bild: "#3B82F6", sonstiges: "#8B5CF6" };
const FILE_CATS = ["rechnung", "bild", "sonstiges"];
const DOC_TABS = ["vertrag", "briefing", "bericht", "dateien"];
const LOC = { de: "de-CH", fr: "fr-CH", it: "it-CH", en: "en-GB" };
const fmtDateLong = (iso, lang) => { try { return new Date(iso).toLocaleDateString(LOC[lang] || "de-CH", { day: "2-digit", month: "short", year: "numeric" }); } catch { return iso; } };
const fmtDateNum = (iso, lang) => { try { return new Date(iso).toLocaleDateString(LOC[lang] || "de-CH", { day: "2-digit", month: "2-digit", year: "numeric" }); } catch { return iso; } };
const fmtSize = (b) => { if (!b) return "—"; if (b < 1024) return `${b} B`; if (b < 1048576) return `${(b / 1024).toFixed(0)} KB`; return `${(b / 1048576).toFixed(1)} MB`; };

export default function CDocsInvoices() {
  const { accent, clientId, overview, base } = useCockpit();
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  const isStaff = ["super_admin", "team_member"].includes(user.role);
  const canInv = isStaff || user.role === "client" || (user.allowed_sections || []).includes("invoices");
  const canDoc = isStaff || user.role === "client" || (user.allowed_sections || []).includes("documents");

  const [invoices, setInvoices] = useState(null);
  const [docs, setDocs] = useState(null);
  const [busy, setBusy] = useState(null);
  const [upgrading, setUpgrading] = useState(false);
  const [tab, setTab] = useState(loc.pathname.endsWith("/invoices") ? "rechnungen" : "alle");
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("alle");
  const [showAllInv, setShowAllInv] = useState(false);
  const [showAllDocs, setShowAllDocs] = useState(false);
  const [showAllAct, setShowAllAct] = useState(false);

  const cidQ = clientId ? `&client_id=${clientId}` : "";
  const cidQ1 = clientId ? `?client_id=${clientId}` : "";

  useEffect(() => {
    if (canInv) api.get(`/cockpit/invoices${cidQ1}`).then((r) => setInvoices(r.data)).catch(() => setInvoices([]));
    else setInvoices([]);
    if (canDoc) api.get(`/cockpit/documents?lang=${lang}${cidQ}`).then((r) => setDocs(r.data)).catch(() => setDocs([]));
    else setDocs([]);
  }, [clientId, lang]); // eslint-disable-line

  const download = async (d) => {
    setBusy(d.id);
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = d.file_name || "datei";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error(t("Download fehlgeschlagen")); } finally { setBusy(null); }
  };
  const openDoc = (d) => window.open(`${API}/documents/${d.id}/download`, "_blank");
  const openInvPdf = (i) => window.open(`${API}/invoices/${i.id}/pdf`, "_blank");
  const downloadInvPdf = async (i) => {
    try {
      const res = await api.get(`/invoices/${i.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${i.number || "Rechnung"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error(t("Download fehlgeschlagen")); }
  };

  const requestUpgrade = async () => {
    setUpgrading(true);
    try {
      await api.post(`/cockpit/request${cidQ1}`, { topic: "Paket-Upgrade", message: `Anfrage für ein Upgrade ab Paket «${overview.package?.name}».` });
      toast.success(t("Danke! Wir prüfen Ihre Anfrage und melden uns mit einer Empfehlung."));
    } catch { toast.error(t("Anfrage konnte nicht gesendet werden.")); } finally { setUpgrading(false); }
  };

  const docLang = (overview.client?.language || "de").toUpperCase();

  const exportData = () => {
    const rows = [[t("Typ"), t("Bezeichnung"), t("Nummer / Datei"), t("Datum"), t("Betrag / Grösse"), t("Status / Sprache")]];
    (invoices || []).forEach((i) => rows.push([t("Rechnung"), i.service_period || "", i.number || "", (i.issue_date || "").slice(0, 10), chfFr(i.gross_chf), i.status_label || i.status]));
    (docs || []).forEach((d) => rows.push([d.category_label || t("Dokument"), d.title || "", d.file_name || "", (d.created_at || "").slice(0, 10), fmtSize(d.file_size), docLang]));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `KEWAGpage_Dokumente_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success(t("Export erstellt — Datei wird heruntergeladen."));
  };

  const activities = useMemo(() => {
    const ev = [];
    (docs || []).forEach((d) => {
      const isReport = d.category === "bericht";
      ev.push({ id: `doc-${d.id}`, kind: isReport ? "report" : "doc", title: d.title || d.file_name, label: isReport ? t("Neuer Report hochgeladen") : t("Neues Dokument hochgeladen"), date: d.created_at });
    });
    (invoices || []).forEach((i) => {
      ev.push({ id: `inv-c-${i.id}`, kind: "invoice", title: `${t("Rechnung")} ${i.service_period || i.number}`, label: t("Rechnung erstellt"), date: i.issue_date });
      if (i.status === "bezahlt") ev.push({ id: `inv-p-${i.id}`, kind: "paid", title: `${t("Rechnung")} ${i.number}`, label: t("Zahlung eingegangen"), date: i.due_date });
    });
    return ev.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
  }, [docs, invoices]); // eslint-disable-line

  if ((canInv && invoices === null) || (canDoc && docs === null)) return <Spinner />;

  const docCount = (cat) => (docs || []).filter((d) => (cat === "dateien" ? FILE_CATS.includes(d.category) : d.category === cat)).length;
  const allCount = (invoices || []).length + (docs || []).length;
  const TABS = [
    { key: "alle", label: t("Alle"), count: allCount, show: true },
    { key: "rechnungen", label: t("Rechnungen"), count: (invoices || []).length, show: canInv },
    { key: "vertrag", label: t("Verträge"), count: docCount("vertrag"), show: canDoc },
    { key: "briefing", label: t("Briefings"), count: docCount("briefing"), show: canDoc },
    { key: "bericht", label: t("Berichte"), count: docCount("bericht"), show: canDoc },
    { key: "dateien", label: t("Dateien"), count: docCount("dateien"), show: canDoc },
  ].filter((x) => x.show);

  const ql = q.trim().toLowerCase();
  const invFiltered = (invoices || []).filter((i) => {
    if (statusF !== "alle" && i.status !== statusF) return false;
    if (ql && !`${i.number} ${i.service_period} ${i.invoice_type_label}`.toLowerCase().includes(ql)) return false;
    return true;
  });
  const docFiltered = (docs || []).filter((d) => {
    if (DOC_TABS.includes(tab)) {
      if (tab === "dateien" ? !FILE_CATS.includes(d.category) : d.category !== tab) return false;
    }
    if (ql && !`${d.title} ${d.file_name} ${d.category_label}`.toLowerCase().includes(ql)) return false;
    return true;
  });

  const showInvSection = canInv && (tab === "alle" || tab === "rechnungen");
  const showDocSection = canDoc && (tab === "alle" || DOC_TABS.includes(tab));
  const invLimit = (tab === "rechnungen" || showAllInv) ? Infinity : 5;
  const docLimit = (DOC_TABS.includes(tab) || showAllDocs) ? Infinity : 5;
  const actShown = showAllAct ? activities : activities.slice(0, 4);

  const openInv = (invoices || []).filter((i) => i.status !== "bezahlt");
  const openAmount = openInv.reduce((s, i) => s + (i.gross_chf || 0), 0);
  const { package: pkg, subscription, included_features, active_modules, billing } = overview;
  const feats = (included_features && included_features.length ? included_features : (pkg?.features || [])) || [];
  const termMonths = subscription?.months;
  const termLabel = termMonths ? `${termMonths} ${t("Monate")}` : "—";
  const totalMonthly = billing?.total_monthly ?? subscription?.price_chf ?? billing?.package_price;

  const relTime = (iso) => {
    try {
      const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
      if (days <= 0) return t("heute");
      if (days === 1) return t("vor 1 Tag");
      if (days < 7) return `${t("vor")} ${days} ${t("Tagen")}`;
      return fmtDateNum(iso, lang);
    } catch { return fmtDateNum(iso, lang); }
  };

  return (
    <div className="fade-in space-y-5" data-testid="cockpit-documents">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-1.5 text-sm mb-2" data-testid="cdi-breadcrumb">
            <FolderOpen className="w-3.5 h-3.5" style={{ color: accent }} />
            <span style={{ color: accent }}>{t("Kunden-Cockpit")}</span>
            <ChevronRight className="w-3.5 h-3.5 text-gray-600" />
            <span className="text-gray-300">{t("Rechnungen")}</span>
          </div>
          <h1 className="text-[26px] lg:text-[34px] leading-tight font-black tracking-tight">{t("Rechnungen")}</h1>
          <p className="text-sm text-gray-400 mt-1.5 max-w-2xl">{t("Ihre Rechnungen, Verträge, Berichte und Unterlagen — übersichtlich an einem Ort.")}</p>
        </div>
        <button onClick={exportData} data-testid="cdi-export"
          className="flex items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-xl border border-white/10 bg-white/[0.04] hover:bg-white/10 transition-colors text-white shrink-0">
          <Upload className="w-4 h-4" /> {t("Daten exportieren")}
        </button>
      </div>

      {/* KPI-Karten */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
        {canInv && <DocKpi icon={Briefcase} tone={accent} label={t("Aktuelles Paket")} value={pkg?.name || "—"} sub={`${chfFr(totalMonthly)} ${t("/ Monat")}`} testid="cdi-kpi-package" />}
        {canInv && <DocKpi icon={ReceiptText} tone="#F59E0B" primary={openInv.length > 0} label={t("Offene Rechnungen")} value={openInv.length} sub={openInv.length ? `${chfFr(openAmount)} ${t("offen")}` : t("alles bezahlt")} testid="cdi-kpi-open" />}
        {canDoc && <DocKpi icon={FileText} tone="#3B82F6" label={t("Dokumente verfügbar")} value={(docs || []).length} sub={t("in allen Kategorien")} testid="cdi-kpi-docs" />}
        {canInv && <DocKpi icon={CalendarDays} tone="#8B5CF6" label={t("Laufzeit bis")} value={subscription?.renewal_date || "—"} sub={termLabel} testid="cdi-kpi-term" />}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_minmax(0,340px)] gap-5 items-start">
        {/* Linke Spalte */}
        <div className="space-y-5 min-w-0">
          {/* Tabs + Suche + Filter */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex gap-2 overflow-x-auto pb-1 flex-1 min-w-0">
              {TABS.map((x) => (
                <button key={x.key} data-testid={`cdi-tab-${x.key}`} onClick={() => { setTab(x.key); setShowAllInv(false); setShowAllDocs(false); }}
                  className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap border transition-colors ${tab === x.key ? "text-black border-transparent" : "text-gray-400 border-white/10 hover:text-white"}`}
                  style={tab === x.key ? { background: accent } : {}}>
                  {x.label}<span className={tab === x.key ? "opacity-60" : "text-gray-500"}>{x.count}</span>
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <div className="relative">
                <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Suchen…")} data-testid="cdi-search"
                  className="bg-white/[0.04] border border-white/10 rounded-xl text-sm text-gray-200 pl-9 pr-3 py-2 w-40 sm:w-52 focus:outline-none focus:border-[color:var(--client-accent)]/50" />
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="cdi-filter" className={`flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-xl border transition-colors ${statusF !== "alle" ? "text-black border-transparent" : "border-white/10 text-gray-300 hover:bg-white/10"}`} style={statusF !== "alle" ? { background: accent } : {}}>
                    <SlidersHorizontal className="w-4 h-4" /> {t("Filter")}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#15181e] border-white/10 text-gray-200">
                  {[["alle", t("Alle Rechnungen")], ["offen", t("Nur offene")], ["bezahlt", t("Nur bezahlte")], ["überfällig", t("Nur überfällige")]].map(([k, l]) => (
                    <DropdownMenuItem key={k} data-testid={`cdi-filter-${k}`} onClick={() => setStatusF(k)} className="cursor-pointer focus:bg-white/10 focus:text-white">
                      {statusF === k && <Check className="w-3.5 h-3.5 mr-1.5" style={{ color: accent }} />}{l}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* RECHNUNGEN */}
          {showInvSection && (
            <Card className="p-5" data-testid="cdi-invoices-section">
              <SectionLabel>{t("Rechnungen")}</SectionLabel>
              {invFiltered.length === 0 ? (
                <EmptyRow t={t} />
              ) : (
                <div className="space-y-3">
                  {invFiltered.slice(0, invLimit).map((i) => (
                    <InvoiceRow key={i.id} i={i} accent={accent} lang={lang} t={t} onOpen={() => openInvPdf(i)} onDownload={() => downloadInvPdf(i)} />
                  ))}
                </div>
              )}
              {tab === "alle" && !showAllInv && invFiltered.length > 5 && (
                <ShowAllBtn testid="cdi-all-invoices" onClick={() => setShowAllInv(true)}>{t("Alle Rechnungen anzeigen")}</ShowAllBtn>
              )}
            </Card>
          )}

          {/* DOKUMENTE */}
          {showDocSection && (
            <Card className="p-5" data-testid="cdi-documents-section">
              <SectionLabel>{t("Dokumente")}</SectionLabel>
              {docFiltered.length === 0 ? (
                <EmptyRow t={t} />
              ) : (
                <>
                {/* Mobile: Karten statt Tabelle */}
                <div className="md:hidden space-y-2.5">
                  {docFiltered.slice(0, docLimit).map((d) => (
                    <DocCard key={d.id} d={d} accent={accent} lang={lang} docLang={docLang} t={t} busy={busy} onOpen={() => openDoc(d)} onDownload={() => download(d)} />
                  ))}
                </div>
                {/* Desktop: Tabelle */}
                <div className="hidden md:block overflow-x-auto -mx-1">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 border-b border-white/10">
                        <th className="font-medium py-2 px-1">{t("Typ")}</th>
                        <th className="font-medium py-2 px-1">{t("Dateiname")}</th>
                        <th className="font-medium py-2 px-1 hidden md:table-cell">{t("Hochgeladen am")}</th>
                        <th className="font-medium py-2 px-1 hidden lg:table-cell">{t("Hochgeladen von")}</th>
                        <th className="font-medium py-2 px-1 hidden sm:table-cell">{t("Sprache")}</th>
                        <th className="font-medium py-2 px-1 text-right">{t("Aktionen")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {docFiltered.slice(0, docLimit).map((d) => {
                        const Icon = CAT_ICON[d.category] || File;
                        const tone = CAT_TONE[d.category] || "#8B5CF6";
                        return (
                          <tr key={d.id} className="border-b border-white/5 last:border-0" data-testid={`cdoc-${d.id}`}>
                            <td className="py-3 px-1">
                              <span className="inline-flex items-center gap-2 text-gray-300">
                                <span className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${tone}1F`, color: tone }}><Icon className="w-4 h-4" /></span>
                                <span className="hidden sm:inline">{d.category_label}</span>
                              </span>
                            </td>
                            <td className="py-3 px-1 text-gray-200 max-w-[240px] truncate" title={d.file_name}>{d.file_name}</td>
                            <td className="py-3 px-1 text-gray-400 hidden md:table-cell">{fmtDateNum(d.created_at, lang)}</td>
                            <td className="py-3 px-1 text-gray-400 hidden lg:table-cell">{d.uploaded_by_name || "KEWAG Team"}</td>
                            <td className="py-3 px-1 text-gray-400 hidden sm:table-cell">{docLang}</td>
                            <td className="py-3 px-1">
                              <div className="flex items-center justify-end gap-1">
                                <IconBtn title={t("Öffnen")} testid={`cdoc-view-${d.id}`} onClick={() => openDoc(d)}><Eye className="w-4 h-4" /></IconBtn>
                                <IconBtn title={t("Herunterladen")} testid={`cdoc-download-${d.id}`} disabled={busy === d.id} onClick={() => download(d)}><Download className="w-4 h-4" /></IconBtn>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-white/10 transition-colors" data-testid={`cdoc-menu-${d.id}`}><MoreVertical className="w-4 h-4" /></button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="bg-[#15181e] border-white/10 text-gray-200">
                                    <DropdownMenuItem onClick={() => openDoc(d)} className="cursor-pointer focus:bg-white/10 focus:text-white">{t("Öffnen")}</DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => download(d)} className="cursor-pointer focus:bg-white/10 focus:text-white">{t("Herunterladen")}</DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                </>
              )}
              {tab === "alle" && !showAllDocs && docFiltered.length > 5 && (
                <ShowAllBtn testid="cdi-all-documents" onClick={() => setShowAllDocs(true)}>{t("Alle Dokumente anzeigen")}</ShowAllBtn>
              )}
            </Card>
          )}
        </div>

        {/* Rechte Spalte */}
        <div className="space-y-4 xl:sticky xl:top-4" data-testid="cdi-rail">
          {canInv && (
            <Card className="p-5">
              <div className="flex items-center gap-2 text-xs text-gray-400"><Briefcase className="w-4 h-4" style={{ color: accent }} /> {t("Ihr Abo & Laufzeit")}</div>
              <div className="text-2xl font-black tracking-tight mt-2">{pkg?.name}</div>
              <div className="text-sm" style={{ color: accent }}>{chfFr(totalMonthly)} {t("/ Monat")} <span className="text-[10px] text-gray-500">{t("inkl. MWST")}</span></div>
              <div className="mt-4 pt-4 border-t border-white/10 space-y-1.5 text-sm">
                <div className="flex items-center justify-between text-gray-400"><span>{t("Laufzeit bis")}</span><span className="text-gray-200 font-semibold">{subscription?.renewal_date || "—"}</span></div>
                <div className="flex items-center justify-between text-gray-400"><span>{t("Mindestlaufzeit")}</span><span className="text-gray-200 font-semibold">{termLabel}</span></div>
              </div>
              {billing?.has_addons && (
                <div className="mt-3 pt-3 border-t border-white/10 space-y-1.5 text-sm" data-testid="cinv-billing-summary">
                  <div className="flex items-center justify-between text-gray-400"><span>{t("Paket")}</span><span>{chfFr(billing.package_price)}</span></div>
                  <div className="flex items-center justify-between text-gray-400"><span>{t("Zusatzmodule")}</span><span>{chfFr(billing.addon_sum)}</span></div>
                  <div className="flex items-center justify-between font-black pt-1"><span>{t("Total monatlich")}</span><span style={{ color: accent }}>{chfFr(billing.total_monthly)}</span></div>
                </div>
              )}
              <BtnPrimary accent={accent} data-testid="inv-upgrade" onClick={requestUpgrade} disabled={upgrading} className="w-full mt-4 !py-2.5">
                <TrendingUp className="w-4 h-4" /> {upgrading ? t("Senden…") : t("Upgrade anfragen")}
              </BtnPrimary>
            </Card>
          )}

          {canInv && feats.length > 0 && (
            <Card className="p-5">
              <SectionLabel>{t("Im Paket enthalten")}</SectionLabel>
              <div className="space-y-2.5">
                {feats.slice(0, 8).map((f, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-gray-300"><Check className="w-4 h-4 shrink-0 mt-0.5" style={{ color: accent }} /> {f}</div>
                ))}
              </div>
              {(active_modules || []).length > 0 && (
                <div className="mt-3 pt-3 border-t border-white/10 space-y-2" data-testid="cinv-active-modules">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500">{t("Aktive Zusatzmodule")}</div>
                  {active_modules.map((m, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2"><Check className="w-3.5 h-3.5 shrink-0" style={{ color: accent }} /> {m.name}</span>
                      <span className="text-gray-400">{billing?.has_addons && m.price_chf ? `${chfFr(m.price_chf)}` : t("Inklusive")}</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}

          {/* Neueste Aktivitäten */}
          {activities.length > 0 && (
            <Card className="p-5" data-testid="cdi-activities">
              <SectionLabel>{t("Neueste Aktivitäten")}</SectionLabel>
              <div className="space-y-3">
                {actShown.map((e) => <ActivityRow key={e.id} e={e} accent={accent} relTime={relTime} />)}
              </div>
              {!showAllAct && activities.length > 4 && (
                <ShowAllBtn testid="cdi-all-activities" onClick={() => setShowAllAct(true)}>{t("Alle Aktivitäten anzeigen")}</ShowAllBtn>
              )}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function DocKpi({ icon: Icon, tone, label, value, sub, testid, primary }) {
  return (
    <div data-testid={testid} className={`rounded-2xl p-4 lg:p-5 border ${primary ? "bg-[#171b14]" : "bg-[#15181e] border-white/5"}`} style={primary ? { borderColor: `${tone}66` } : {}}>
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${tone}1F`, color: tone }}><Icon className="w-5 h-5" /></span>
        <span className="text-[13px] font-medium text-gray-400 leading-tight">{label}</span>
      </div>
      <div className="text-[24px] lg:text-[28px] font-black tracking-tight mt-3 leading-none truncate">{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-1.5 truncate">{sub}</div>}
    </div>
  );
}

function InvStatus({ status, t }) {
  if (status === "bezahlt") return <span className="text-[11px] font-bold px-2.5 py-0.5 rounded-full border border-[#A3E635]/40 text-[#A3E635] bg-[#A3E635]/10">{t("Bezahlt")}</span>;
  if (status === "überfällig") return <span className="text-[11px] font-bold px-2.5 py-0.5 rounded-full border border-red-500/40 text-red-300 bg-red-500/10">{t("Überfällig")}</span>;
  return <span className="text-[11px] font-bold px-2.5 py-0.5 rounded-full border border-amber-400/40 text-amber-300 bg-amber-400/10">{t("Offen")}</span>;
}

function InvoiceRow({ i, accent, lang, t, onOpen, onDownload }) {
  const paid = i.status === "bezahlt";
  return (
    <div className="flex items-center gap-4 rounded-xl border border-white/[0.06] bg-white/[0.02] p-3.5" data-testid={`cinv-row-${i.id}`}>
      <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: paid ? "#A3E63514" : "#F59E0B18", color: paid ? accent : "#F59E0B" }}>
        <ReceiptText className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold truncate">{i.service_period || t(i.invoice_type_label)}</span>
          <InvStatus status={i.status} t={t} />
        </div>
        <div className="text-xs text-gray-500 mt-0.5 truncate">{i.number}</div>
      </div>
      <div className="hidden md:block w-36 shrink-0">
        <div className="text-[11px] text-gray-500">{paid ? t("Bezahlt am") : t("Fällig am")}</div>
        <div className="text-sm text-gray-200 mt-0.5">{fmtDateLong(i.due_date || i.issue_date, lang)}</div>
      </div>
      <div className="hidden sm:block w-28 shrink-0">
        <div className="text-[11px] text-gray-500">{t("Betrag")}</div>
        <div className="text-sm font-bold mt-0.5">{chfFr(i.gross_chf)}</div>
      </div>
      <button onClick={onOpen} data-testid={`cinv-pdf-${i.id}`}
        className="shrink-0 flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-xl border" style={{ color: accent, borderColor: `${accent}55` }}>
        <Download className="w-3.5 h-3.5" /> PDF
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="shrink-0 p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-white/10 transition-colors" data-testid={`cinv-menu-${i.id}`}><MoreVertical className="w-4 h-4" /></button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-[#15181e] border-white/10 text-gray-200">
          <DropdownMenuItem onClick={onOpen} className="cursor-pointer focus:bg-white/10 focus:text-white">{t("PDF öffnen")}</DropdownMenuItem>
          <DropdownMenuItem onClick={onDownload} className="cursor-pointer focus:bg-white/10 focus:text-white">{t("PDF herunterladen")}</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

const ACT_ICON = { report: { Icon: FileText, c: "#8B5CF6" }, doc: { Icon: FileText, c: "#3B82F6" }, invoice: { Icon: ReceiptText, c: "#F59E0B" }, paid: { Icon: Check, c: "#A3E635" } };
function ActivityRow({ e, relTime }) {
  const { Icon, c } = ACT_ICON[e.kind] || ACT_ICON.doc;
  return (
    <div className="flex items-start gap-3">
      <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5" style={{ background: `${c}1F`, color: c }}><Icon className="w-4 h-4" /></span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-gray-200 truncate">{e.title}</div>
        <div className="text-xs text-gray-500 truncate">{e.label}</div>
      </div>
      <span className="text-[11px] text-gray-500 shrink-0 whitespace-nowrap">{relTime(e.date)}</span>
    </div>
  );
}

const IconBtn = ({ children, title, testid, onClick, disabled }) => (
  <button title={title} data-testid={testid} onClick={onClick} disabled={disabled}
    className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-40">{children}</button>
);

const DocCard = ({ d, accent, lang, docLang, t, busy, onOpen, onDownload }) => {
  const Icon = CAT_ICON[d.category] || File;
  const tone = CAT_TONE[d.category] || "#8B5CF6";
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-3.5" data-testid={`cdoc-m-${d.id}`}>
      <span className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${tone}1F`, color: tone }}><Icon className="w-5 h-5" /></span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-gray-100 truncate" title={d.file_name}>{d.file_name}</div>
        <div className="text-[11px] text-gray-500 truncate mt-0.5">{d.category_label} · {fmtDateNum(d.created_at, lang)} · {docLang}</div>
      </div>
      <button onClick={onOpen} data-testid={`cdoc-m-view-${d.id}`} title={t("Öffnen")} aria-label={t("Öffnen")}
        className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center border border-white/10 text-gray-300 hover:bg-white/10 active:scale-95 transition"><Eye className="w-4 h-4" /></button>
      <button onClick={onDownload} disabled={busy === d.id} data-testid={`cdoc-m-download-${d.id}`} title={t("Herunterladen")} aria-label={t("Herunterladen")}
        className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center border disabled:opacity-40 active:scale-95 transition" style={{ color: accent, borderColor: `${accent}55` }}><Download className="w-4 h-4" /></button>
    </div>
  );
};


const ShowAllBtn = ({ children, onClick, testid }) => (
  <div className="flex justify-center mt-4">
    <button onClick={onClick} data-testid={testid}
      className="text-sm font-semibold text-gray-300 px-5 py-2 rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/10 transition-colors">{children}</button>
  </div>
);

const EmptyRow = ({ t }) => (
  <div className="flex flex-col items-center text-center py-8">
    <Inbox className="w-8 h-8 text-gray-700 mb-2" />
    <div className="text-sm font-semibold text-gray-300">{t("Keine Einträge in dieser Ansicht.")}</div>
    <p className="text-xs text-gray-500 mt-1">{t("Sobald KEWAG etwas für Sie bereitstellt, erscheint es hier.")}</p>
  </div>
);
